import { ExtendedAxiosError } from '@issuemanager/apps/client/hooks';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { type Relationship } from './useIssueOutletContext';

// Mock data for demonstration purposes (RCP-29354)
// Based on Figma design with realistic GRC object IDs
const MOCK_RELATIONSHIPS: Relationship[] = [
  {
    id: 'R7895',
    code: 'R7895',
    type: 'Risk',
    title: 'Hurricane Ian–Related Catastrophe Losses',
    link: '#',
  },
  {
    id: 'REQ23079122',
    code: 'REQ23079122',
    type: 'Risk',
    title: 'Ransomware Threat from LockBit Group',
    link: '#',
  },
  {
    id: 'REQ23076129',
    code: 'REQ23076129',
    type: 'Risk',
    title: 'California Wildfire Season 2025',
    link: '#',
  },
  {
    id: 'REQ23036122',
    code: 'REQ23036122',
    type: 'Risk mitigation',
    title: 'Real-Time Monitoring',
    link: '#',
  },
  {
    id: 'REQ23076120',
    code: 'REQ23076120',
    type: 'Control',
    title: 'Catastrophe Reinsurance Coverage Control',
    link: '#',
  },
  {
    id: 'REQ23073122',
    code: 'REQ23073122',
    type: 'Control assessment',
    title: 'Hurricane Ian–Related Catastrophe Losses',
    link: '#',
  },
  {
    id: 'REQ23070122',
    code: 'REQ23070122',
    type: 'Process',
    title: 'Ransomware Threat from LockBit Group',
    link: '#',
  },
  {
    id: 'REQ23076122',
    code: 'REQ23076122',
    type: 'Process',
    title: 'California Wildfire Season 2025',
    link: '#',
  },
  {
    id: 'REQ23076127',
    code: 'REQ23076127',
    type: 'Process',
    title: 'Catastrophe Reinsurance Coverage Control',
    link: '#',
  },
];

/**
 * Hook to fetch issue relationships from the backend.
 * Currently returns mock data for demonstration purposes (RCP-29354).
 *
 * @param issueCode - The issue code to fetch relationships for
 * @returns Query result containing relationships array
 *
 * TODO: Replace mock data with actual API call when backend endpoint is available
 */
export function useIssueRelationships(
  issueCode?: string,
): UseQueryResult<Relationship[], ExtendedAxiosError> {
  return useQuery<Relationship[], ExtendedAxiosError>({
    queryKey: ['issue-relationships', issueCode],
    queryFn: async () =>
      // TODO: Implement actual API call
      Promise.resolve(MOCK_RELATIONSHIPS),
    enabled: !!issueCode,
  });
}

type RemoveRelationshipContext = {
  previousRelationships: Relationship[] | undefined;
};

/**
 * Hook to remove a relationship from an issue.
 * Uses optimistic updates for immediate UI feedback.
 *
 * @param issueCode - The issue code to remove the relationship from
 * @returns Mutation object for removing relationships
 */
export function useRemoveRelationship(
  issueCode: string,
): UseMutationResult<
  void,
  ExtendedAxiosError,
  string,
  RemoveRelationshipContext
> {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    ExtendedAxiosError,
    string,
    RemoveRelationshipContext
  >({
    mutationKey: ['issue-relationships', issueCode, 'remove'],
    mutationFn: async (_relationshipId) =>
      // TODO: Implement actual API call
      Promise.resolve(),
    onMutate: async (relationshipId) => {
      await queryClient.cancelQueries({
        queryKey: ['issue-relationships', issueCode],
      });

      const previousRelationships = queryClient.getQueryData<Relationship[]>([
        'issue-relationships',
        issueCode,
      ]);

      if (previousRelationships) {
        queryClient.setQueryData<Relationship[]>(
          ['issue-relationships', issueCode],
          previousRelationships.filter((rel) => rel.id !== relationshipId),
        );
      }

      return { previousRelationships };
    },
    onError: (_error, _relationshipId, context) => {
      if (context?.previousRelationships) {
        queryClient.setQueryData<Relationship[]>(
          ['issue-relationships', issueCode],
          context.previousRelationships,
        );
      }
    },
    // TODO: Uncomment this when the backend endpoint is implemented
    /*     onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['issue-relationships', issueCode],
      });
    }, */
  });
}
